import { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { resetConfiguration } from "@/tools/helper";

const RESET_COOLDOWN_MS = 20000; // 20 seconds

interface ResetSessionData {
  configID: string;
  language: string;
  timestamp: number;
  disabledUntil?: number;
}

export interface UseResetConfigurationResult {
  configID?: string;
  language?: string;
  error?: Error;
  isLoading?: boolean;
  reset: () => Promise<{ configID: string; language: string } | undefined>;
  hasData: boolean;
  isResetDisabled: boolean;
}

/**
 * Hook for resetting configuration
 *
 * @description Provides functionality to reset the current configuration to its initial state.
 * Manages its own persistent state and provides utilities for state management.
 *
 * @returns Object containing:
 *   - configID: The new configuration ID from the reset response
 *   - language: The language from the reset response
 *   - error: Any error that occurred during the reset operation
 *   - isLoading: Loading state of the reset mutation
 *   - reset: Async function to trigger the reset operation. Returns a Promise with the new configID and language
 *   - hasData: Boolean indicating if there's any reset session data
 *   - isResetDisabled: Boolean indicating if reset is currently disabled (20s cooldown after successful reset)
 *
 * @example
 * ```typescript
 * const { configID, language, reset, isLoading, hasData } = useResetConfiguration();
 * ```
 */
export const useResetConfiguration = (): UseResetConfigurationResult => {
  const queryClient = useQueryClient();

  const {
    data: resetData,
    error,
    isPending: isLoading,
    mutateAsync: reset,
  } = useMutation({
    mutationKey: ["resetConfiguration"],
    mutationFn: async () => {
      try {
        const result = await resetConfiguration();
        return result;
      } catch (error) {
        console.error("Failed to reset configuration:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      if (!data) return;

      const now = Date.now();
      queryClient.setQueryData(["resetSessionData"], {
        ...data,
        timestamp: now,
        disabledUntil: now + RESET_COOLDOWN_MS,
      });
      queryClient.removeQueries({ queryKey: ["loadedSessionData"] });
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  const { data: persistedData } = useQuery({
    queryKey: ["resetSessionData"],
    queryFn: () => queryClient.getQueryData(["resetSessionData"]),
    enabled: true,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const persisted = persistedData as ResetSessionData | undefined;

  const { configID, language, hasData } = useMemo(
    () => ({
      configID: persisted?.configID ?? resetData?.configID,
      language: persisted?.language ?? resetData?.language,
      hasData: !!(persisted?.configID ?? resetData?.configID),
    }),
    [persisted, resetData],
  );

  // [KS] State to force re-render when disabled period expires
  const [, forceUpdate] = useState(0);

  const isResetDisabled = persisted?.disabledUntil
    ? // eslint-disable-next-line react-hooks/purity -- live cooldown check against wall-clock time, re-evaluated via the forceUpdate timer below
      Date.now() < persisted.disabledUntil
    : false;

  useEffect(() => {
    if (!persisted?.disabledUntil) return;

    const remainingTime = persisted.disabledUntil - Date.now();
    if (remainingTime <= 0) return;

    const timer = setTimeout(
      () => forceUpdate((prev) => prev + 1),
      remainingTime,
    );
    return () => clearTimeout(timer);
  }, [persisted?.disabledUntil]);

  return {
    configID,
    language,
    error: error ?? undefined,
    isLoading,
    reset,
    hasData,
    isResetDisabled,
  };
};
